import React, { useEffect } from "react";
import { Navigate, Routes, Route } from "react-router-dom";
import { Layout, Spin } from 'antd';
import { fetchCartItemsCount, fetchCurrentUser, fetchLikedProductCount } from "../../services";
import { useAppDispatch, useAppSelector } from "../../hooks/redux";
import { loadGuestCheckout } from "../../redux/slices/shoppingCartSlice";
import { loadGuestLiked } from "../../redux/slices/likedProductSlice";

import AppHeader from "../appHeader";
import ProductType from "../productType";
import ProductGender from "../productGender";
import AllProducts from "../allProducts";
import HomePage from "../homePage";
import Footer from "../footer";
import ProductById from "../productById";
import UserPage from "../userLayout";
import LikedProduct from "../likedProduct/likedProduct";
import ShoppingCartPage from "../shoppingCartPage";
import CheckoutPage from "../сheckoutPage";
import PaymentPage from "../paymentPage";
import PageNotFound from "../pageNotFound";
import HelpPage from "../helpPage";
import AboutUs from "../aboutUsPage/aboutUsPage";
import OrderHistory  from "../orderHistory/orderHistory";
import Shipping from "../shipping";
import UserProfile from "../userProfile";
import ReviewConfirmPage from "../reviewConfirmPage";
import CheckoutCancelPage from '../checkoutCancelPage/checkoutCancelPage';
import './appRouter.scss';
import AdminOrders from '../adminOrders/AdminOrders/AdminOrders';
import AdminProductForm from '../adminProductForm';
import AdminUsers from '../adminUsers';
import LegalPage from '../legalPage';
import CookieConsentBanner from '../cookieConsent';
import AnalyticsGate from '../cookieConsent/AnalyticsGate';

const { Content } = Layout;

const AdminRoute = ({ children }: { children: React.ReactElement }) => {
  const { user, authChecked } = useAppSelector((state) => state.user);

  if (!authChecked && !user) {
    return <Spin />;
  }

  if (!user || typeof user.isAdmin !== 'boolean' || !user.isAdmin) {
    return <Navigate to="/UserPage" replace />;
  }

  return children;
};

const AppRouter = () => {
  const dispatch = useAppDispatch();
  const { user, isAuthenticated, authChecked } = useAppSelector((state) => state.user);

  useEffect(() => {
    if (user === null && !isAuthenticated) {
      dispatch(fetchCurrentUser());
    }
  }, [dispatch, user, isAuthenticated]);

  useEffect(() => {
    if (user?.id) {
      dispatch(fetchCartItemsCount(user?.id));
      dispatch(fetchLikedProductCount(user.id));
    } else if (authChecked) {
      dispatch(loadGuestCheckout());
      dispatch(loadGuestLiked());
    }
  }, [authChecked, dispatch, user]);

  return (
    <Layout>
      <div className="container">
      <AppHeader />
      <Content className="site-content">
          <Routes>
            <Route path="/" element={
              <HomePage />
            } />
            <Route path="/help" element={
            <HelpPage />}
            />
            <Route path="/about-us" element={
            <AboutUs />}
            />
            <Route path="/privacy" element={<LegalPage page="privacy" />} />
            <Route path="/terms" element={<LegalPage page="terms" />} />
            <Route path="/returns" element={<LegalPage page="returns" />} />
            <Route path="/shipping" element={<LegalPage page="shipping" />} />
            <Route path="/support" element={<LegalPage page="support" />} />
            <Route path="/cookies" element={<LegalPage page="cookies" />} />
            <Route path="/accessibility" element={<LegalPage page="accessibility" />} />
            <Route path="product/liked" element={
              <LikedProduct />
            } />
            <Route path="/products/types/:type" element={
              <ProductType />
            } />
            <Route path="/products/gender/:gender" element={
              <ProductGender />
            } />
            <Route path="/products" element={
              <AllProducts />
            } />
            <Route path="/products/:id" element={
              <ProductById />
            } />
            <Route path="/cart" element={<ShoppingCartPage />}>
              <Route path="checkout" element={<CheckoutPage />}>
                <Route path="payment" element={<PaymentPage />} />
                <Route path="review-confirm" element={<ReviewConfirmPage />} />
              </Route>
            </Route>
            <Route path="/checkout/success" element={<ReviewConfirmPage />} />
            <Route path="/checkout/cancel" element={<CheckoutCancelPage />} />
            <Route path="/UserPage" element={<UserPage />}>
              <Route index element={<UserProfile />} />
              <Route path="order-history" element={<OrderHistory />} />
              <Route path="shipping-address" element={<Shipping />} />
              <Route path="admin/orders" element={<AdminRoute><AdminOrders /></AdminRoute>} />
              <Route path="admin/products" element={<AdminRoute><AdminProductForm /></AdminRoute>} />
              <Route path="admin/products/create" element={<AdminRoute><AdminProductForm createOnly /></AdminRoute>} />
              <Route path="admin/users" element={<AdminRoute><AdminUsers /></AdminRoute>} />
            </Route>
            <Route path="*" element={<PageNotFound />} />
          </Routes>
      </Content>
      <Footer />
      <CookieConsentBanner />
      <AnalyticsGate />
        </div>
    </Layout>
  );
}

export default AppRouter;
